// store/slices/tokenSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from '../utils/axios'; // Use the axios instance with interceptors
import { PersonalAccessToken } from '../models/PersonalAccessToken';
import { CreateTokenRequest } from '../models/CreateTokenRequest';
import { CreatePersonalAccessTokenResultDto } from '../models/CreatePersonalAccessTokenResultDto';

interface TokenState {
  tokens: PersonalAccessToken[];
  loading: boolean;
  error: string | null;
  createdToken: CreatePersonalAccessTokenResultDto | null;
}

const initialState: TokenState = {
  tokens: [],
  loading: false,
  error: null,
  createdToken: null,
};

// Async thunks
export const fetchTokens = createAsyncThunk(
  'tokens/fetchTokens',
  async (params: { skip: number; take: number }) => {
    const response = await axios.get('/api/services/app/PersonalAccessToken/GetUserTokens', {
      params: {
        skipCount: params.skip,
        maxResultCount: params.take,
      },
    });
    return response.data.result;
  }
);

export const createToken = createAsyncThunk(
  'tokens/createToken',
  async (data: CreateTokenRequest) => {
    const response = await axios.post('/api/services/app/PersonalAccessToken/CreateToken', data);
    return response.data.result;
  }
);

export const revokeToken = createAsyncThunk(
  'tokens/revokeToken',
  async (id: number) => {
    const response = await axios.post('/api/services/app/PersonalAccessToken/RevokeToken', {
      id,
      revocationReason: 'Revoked by user',
      notifyUser: false,
    });
    return response.data;
  }
);

export const deleteToken = createAsyncThunk(
  'tokens/deleteToken',
  async (id: number) => {
    await axios.delete(`/api/services/app/PersonalAccessToken/DeleteToken`, {
      data: { id },
    });
    return id;
  }
);

const tokenSlice = createSlice({
  name: 'tokens',
  initialState,
  reducers: {
    clearTokenCreationResult: (state) => {
      state.createdToken = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Tokens
      .addCase(fetchTokens.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTokens.fulfilled, (state, action) => {
        state.loading = false;
        state.tokens = action.payload.items || [];
      })
      .addCase(fetchTokens.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch tokens';
      })
      // Create Token
      .addCase(createToken.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createToken.fulfilled, (state, action) => {
        state.loading = false;
        state.createdToken = action.payload;
      })
      .addCase(createToken.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create token';
      })
      // Revoke Token
      .addCase(revokeToken.pending, (state) => {
        state.loading = true;
      })
      .addCase(revokeToken.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(revokeToken.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to revoke token';
      })
      // Delete Token
      .addCase(deleteToken.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteToken.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(deleteToken.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete token';
      });
  },
});

export const { clearTokenCreationResult, clearError } = tokenSlice.actions;
export default tokenSlice.reducer;